import { createComponent, ApplicationRef, ComponentRef, inject } from '@angular/core';
import { EsriViewType, Plugin } from '@jeppesen-foreflight/jmkw-types';
import { JmkwApiService } from '@jeppesen-foreflight/jmkw-core';
import { ZoomLevelDisplayComponent } from './zoom-level-display-component';


export class ZoomLevelPlugin extends Plugin {
  private appRef!: ApplicationRef;
  private apiService!: JmkwApiService;
  private _addTimeout?: ReturnType<typeof setTimeout>;
  private readonly _componentRefs = new Map<
    EsriViewType,
    ComponentRef<ZoomLevelDisplayComponent>
  >();

  constructor(
  ) {
    super({
      id: 'zoom_level'
    });
    this.appRef = inject(ApplicationRef);
    this.apiService = inject(JmkwApiService);
  }

  zoomWidgetLoaded = false;

  addZoomComponent(): void {
    if (this.zoomWidgetLoaded) return;
    this.zoomWidgetLoaded = true;

    // Defer to a macrotask so Angular's effect() in BasemapSelectorComponent
    // has run first (effects flush during the preceding CD cycle). This
    // guarantees basemap(0) → zoom-chip(1) → native-zoom(2) in the UI stack.
    this._addTimeout = setTimeout(() => {
      this.mount('2D');
      this.mount('3D');
    });
  }

  private mount(viewType: EsriViewType): void {
    const view =
      viewType === '2D'
        ? this.apiService.getMapView()
        : this.apiService.getSceneView();
    if (!view?.ui) return;

    const componentRef = createComponent(ZoomLevelDisplayComponent, {
      environmentInjector: this.appRef.injector
    });
    componentRef.setInput('viewType', viewType);
    this.appRef.attachView(componentRef.hostView);
    this._componentRefs.set(viewType, componentRef);

    view.ui.add(componentRef.location.nativeElement, {
      position: 'top-left',
      index: 1
    });
  }

  destroy(): void {
    clearTimeout(this._addTimeout);
    for (const [viewType, componentRef] of this._componentRefs) {
      // componentRef.destroy() tears down Angular bookkeeping but doesn't
      // remove the host element from view.ui's container or its internal
      // registry. The element stays visible (frozen at its last state) and
      // stale entries can accumulate across plugin destroy/recreate cycles
      // (HMR, dynamic plugin reloading). Calling view.ui.remove() first
      // ensures full cleanup. Mirrors BasemapSelectorComponent.ngOnDestroy.
      const view =
        viewType === '2D'
          ? this.apiService.getMapView()
          : this.apiService.getSceneView();
      view?.ui?.remove(componentRef.location.nativeElement);
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
    this._componentRefs.clear();
  }
}
